import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AuthenticationService } from '@app/_services';
import { QbSyncService } from './qb-sync.service';
import {
    QbDepositSummary,
    QbDepositLine,
    QbMatchResult,
    QbApplyLineRequest,
    QbApplyRequest,
    QbAuditEntry,
    QbMatchCandidate,
} from './qb-sync.model';

interface LineRow extends QbDepositLine {
    applyChecked: boolean;
    selectedCandidate?: QbMatchCandidate;
    manualEntity?: string;
    manualClass?: string;
    manualAccount?: string;
}

@Component({
    selector: 'app-qb-sync-list',
    templateUrl: './qb-sync-list.component.html',
    styleUrls: ['./qb-sync-list.component.css'],
    providers: [MessageService],
    standalone: false,
})
export class QbSyncListComponent implements OnInit {

    // --- date range (default: last 7 days) ---
    startDate: Date;
    endDate: Date;

    // --- deposit selector ---
    deposits: QbDepositSummary[] = [];
    depositsLoading = false;
    depositsLoadedOnce = false;

    // --- enrichment section ---
    matchResult: QbMatchResult | null = null;
    lineRows: LineRow[] = [];
    matchLoading = false;
    applyLoading = false;

    // --- apply panel ---
    approvedBy = '';

    // --- audit log ---
    auditEntries: QbAuditEntry[] = [];
    auditLoading = false;
    auditVisible = false;

    // --- manual label dialog ---
    manualDialogVisible = false;
    manualLine: LineRow | null = null;
    manualEntityInput = '';
    manualClassInput = '';
    manualAccountInput = '';

    constructor(
        private qbSyncService: QbSyncService,
        private authService: AuthenticationService,
        private messageService: MessageService,
    ) { }

    ngOnInit(): void {
        // Date-only defaults — strip time component (CLAUDE.md rule: no bare new Date())
        const now = new Date();
        this.endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        this.startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);

        const user = this.authService.currentUserValue;
        if (user) {
            this.approvedBy = user['username'] || user['email'] || '';
        }
    }

    // ---- Deposit Selector ----

    loadUnlabeledDeposits(): void {
        if (!this.startDate || !this.endDate) {
            this.messageService.add({ severity: 'warn', summary: 'Date range required', life: 3000 });
            return;
        }
        this.depositsLoading = true;
        this.deposits = [];
        this.matchResult = null;
        this.lineRows = [];
        this.auditEntries = [];
        this.auditVisible = false;

        const start = this.formatDate(this.startDate);
        const end = this.formatDate(this.endDate);

        this.qbSyncService.getUnlabeledDeposits(start, end).subscribe({
            next: (res: any) => {
                this.deposits = res.data?.deposits || res.data || [];
                this.depositsLoading = false;
                this.depositsLoadedOnce = true;
                if (this.deposits.length === 0) {
                    this.messageService.add({ severity: 'info', summary: 'No unlabeled deposits found in that date range.', life: 4000 });
                }
            },
            error: (err: any) => {
                this.depositsLoading = false;
                this.depositsLoadedOnce = true;
                this.messageService.add({ severity: 'error', summary: 'Failed to load deposits', detail: err?.message || JSON.stringify(err), life: 8000 });
            },
        });
    }

    formatDate(d: Date | null | undefined): string {
        if (!d) { return '—'; }
        return this.toIsoDate(d);
    }

    private toIsoDate(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    openDeposit(deposit: QbDepositSummary): void {
        this.matchResult = null;
        this.lineRows = [];
        this.auditEntries = [];
        this.auditVisible = false;
        this.matchLoading = true;

        this.qbSyncService.getMatch(deposit.qboDepositId).subscribe({
            next: (res: any) => {
                const result: QbMatchResult = res.data?.matchResult || res.data;
                this.matchResult = result;
                this.lineRows = (result.lines || []).map(line => this.toLineRow(line));
                this.matchLoading = false;
            },
            error: (err: any) => {
                this.matchLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Failed to load deposit lines', detail: err?.message || JSON.stringify(err), life: 5000 });
            },
        });
    }

    private toLineRow(line: QbDepositLine): LineRow {
        const row: LineRow = {
            ...line,
            applyChecked: !line.labeled && line.confidence === 'HIGH',
            selectedCandidate: (line.candidates && line.candidates.length > 0) ? line.candidates[0] : undefined,
        };
        return row;
    }

    // ---- Footer balance check ----

    get sumOfLines(): number {
        if (!this.lineRows.length) { return 0; }
        return this.lineRows.reduce((acc, r) => acc + (r.amount || 0), 0);
    }

    get bankTotal(): number {
        return this.matchResult?.totalAmt || 0;
    }

    get balanceDiff(): number {
        return Math.abs(this.sumOfLines - this.bankTotal);
    }

    get isPennyPerfect(): boolean {
        return this.balanceDiff < 0.005;
    }

    // ---- Candidate selection ----

    candidateOptions(row: LineRow): { label: string; value: QbMatchCandidate }[] {
        if (!row.candidates) { return []; }
        return row.candidates.map(c => ({
            label: `${c.guestFirstName} ${c.guestLastName} — ${c.confirmationNumber} (${c.propertyCode}, ${c.checkInDate})`,
            value: c,
        }));
    }

    // ---- Manual label dialog ----

    openManualDialog(row: LineRow): void {
        this.manualLine = row;
        this.manualEntityInput = row.manualEntity || row.suggestedEntity || '';
        this.manualClassInput = row.manualClass || row.suggestedClass || '';
        this.manualAccountInput = row.manualAccount || row.suggestedAccount || '';
        this.manualDialogVisible = true;
    }

    saveManualLabel(): void {
        if (!this.manualLine) { return; }
        this.manualLine.manualEntity = this.manualEntityInput;
        this.manualLine.manualClass = this.manualClassInput;
        this.manualLine.manualAccount = this.manualAccountInput;
        this.manualLine.applyChecked = !!(this.manualEntityInput && this.manualClassInput && this.manualAccountInput);
        this.manualDialogVisible = false;
        this.manualLine = null;
    }

    cancelManualDialog(): void {
        this.manualDialogVisible = false;
        this.manualLine = null;
    }

    // ---- Apply ----

    get selectedLineCount(): number {
        return this.lineRows.filter(r => !r.labeled && r.applyChecked).length;
    }

    applySelected(): void {
        if (!this.matchResult) { return; }
        if (!this.approvedBy?.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Enter "Approved by" name before applying.', life: 3000 });
            return;
        }

        const linesToApply = this.lineRows.filter(r => !r.labeled && r.applyChecked);
        if (linesToApply.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No lines selected.', life: 3000 });
            return;
        }

        const applyLines: QbApplyLineRequest[] = linesToApply.map(r => {
            const isManual = !!(r.manualEntity || r.manualClass || r.manualAccount);
            const candidate = r.selectedCandidate;
            return {
                lineNum: r.lineNum,
                entity: isManual ? r.manualEntity : (r.suggestedEntity || ''),
                qbClass: isManual ? r.manualClass : (r.suggestedClass || ''),
                account: Number(isManual ? r.manualAccount : (r.suggestedAccount || '0')),
                reservationId: candidate?.reservationId,
                paymentConfirmId: candidate?.paymentConfirmId,
                confidence: isManual ? 'MANUAL' : (r.confidence || 'LOW'),
            };
        });

        const user = this.authService.currentUserValue;
        const request: QbApplyRequest = {
            lines: applyLines,
            approvedBy: this.approvedBy.trim(),
            userId: user ? (user['userId'] || user['id'] || 0) : 0,
        };

        this.applyLoading = true;
        this.qbSyncService.applyLabels(this.matchResult.qboDepositId, request).subscribe({
            next: (res: any) => {
                this.applyLoading = false;
                this.messageService.add({ severity: 'success', summary: 'Labels applied', detail: `${linesToApply.length} line(s) labeled in QBO.`, life: 4000 });
                // reload the deposit to reflect new labels
                this.openDeposit({ qboDepositId: this.matchResult.qboDepositId } as QbDepositSummary);
            },
            error: (err: any) => {
                this.applyLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Apply failed', detail: err?.message || JSON.stringify(err), life: 6000 });
            },
        });
    }

    // ---- Audit log ----

    toggleAudit(): void {
        this.auditVisible = !this.auditVisible;
        if (this.auditVisible && this.auditEntries.length === 0 && this.matchResult) {
            this.loadAudit();
        }
    }

    loadAudit(): void {
        if (!this.matchResult) { return; }
        this.auditLoading = true;
        this.qbSyncService.getAuditLog(this.matchResult.qboDepositId, undefined, 0, 50).subscribe({
            next: (res: any) => {
                this.auditEntries = res.data?.auditEntries || res.data || [];
                this.auditLoading = false;
            },
            error: (err: any) => {
                this.auditLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Failed to load audit log', detail: err?.message, life: 4000 });
            },
        });
    }

    // ---- Helpers ----

    formatCurrency(val: number): string {
        if (val == null) { return '—'; }
        return val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
}
